import { strings } from "./strings.js";

export type JoinAdmissionLogicalState =
  | "grant-verified"
  | "soft-fail-unavailable"
  | "fallback-pending"
  | "established";

export type JoinAdmissionDenialCode =
  | "GRANT_REQUIRED"
  | "SEAT_CAP_EXCEEDED"
  | "SESSION_STATEMENT_INVALID"
  | "SESSION_MISMATCH"
  | "ADMISSION_DENIED";

export type JoinAdmissionUxDisposition =
  | {
      readonly kind: "state";
      readonly state: JoinAdmissionLogicalState;
    }
  | {
      readonly kind: "denied";
      readonly code: JoinAdmissionDenialCode;
    };

export interface JoinAdmissionPresentation {
  readonly key: JoinAdmissionLogicalState | `denied:${JoinAdmissionDenialCode}`;
  readonly title: string;
  readonly detail: string;
  readonly tone: "verified" | "degraded" | "denied";
}

export function joinAdmissionPresentation(
  disposition: JoinAdmissionUxDisposition,
): JoinAdmissionPresentation {
  if (disposition.kind === "state") {
    switch (disposition.state) {
      case "grant-verified":
        return {
          key: disposition.state,
          title: strings.admission.grantVerified.title,
          detail: strings.admission.grantVerified.detail,
          tone: "verified",
        };
      case "soft-fail-unavailable":
        return {
          key: disposition.state,
          title: strings.admission.softFailUnavailable.title,
          detail: strings.admission.softFailUnavailable.detail,
          tone: "degraded",
        };
      case "fallback-pending":
        return {
          key: disposition.state,
          title: strings.admission.fallbackPending.title,
          detail: strings.admission.fallbackPending.detail,
          tone: "degraded",
        };
      case "established":
        return {
          key: disposition.state,
          title: strings.admission.establishedWanIndependent.title,
          detail: strings.admission.establishedWanIndependent.detail,
          tone: "verified",
        };
    }
  }

  switch (disposition.code) {
    case "GRANT_REQUIRED":
      return {
        key: `denied:${disposition.code}`,
        title: strings.admission.grantRequired.title,
        detail: strings.admission.grantRequired.detail,
        tone: "denied",
      };
    case "SEAT_CAP_EXCEEDED":
      return {
        key: `denied:${disposition.code}`,
        title: strings.admission.seatCapExceeded.title,
        detail: strings.admission.seatCapExceeded.detail,
        tone: "denied",
      };
    case "SESSION_STATEMENT_INVALID":
      return {
        key: `denied:${disposition.code}`,
        title: strings.admission.sessionStatementInvalid.title,
        detail: strings.admission.sessionStatementInvalid.detail,
        tone: "denied",
      };
    case "SESSION_MISMATCH":
      return {
        key: `denied:${disposition.code}`,
        title: strings.admission.sessionMismatch.title,
        detail: strings.admission.sessionMismatch.detail,
        tone: "denied",
      };
    case "ADMISSION_DENIED":
      return {
        key: `denied:${disposition.code}`,
        title: strings.admission.admissionDenied.title,
        detail: strings.admission.admissionDenied.detail,
        tone: "denied",
      };
  }
}

export function JoinAdmissionStatus({
  disposition,
}: {
  readonly disposition?: JoinAdmissionUxDisposition;
}): React.JSX.Element | null {
  if (disposition === undefined) return null;
  const presentation = joinAdmissionPresentation(disposition);
  return (
    <aside
      className="join-admission-status"
      data-join-admission-key={presentation.key}
      data-join-admission-tone={presentation.tone}
      role={disposition.kind === "denied" ? "alert" : "status"}
      aria-label={strings.admission.ariaLabel}
    >
      <strong>{presentation.title}</strong>
      <span>{presentation.detail}</span>
    </aside>
  );
}
